import { useEffect, useState } from "react"
import {
	IconPlusOutline18 as Plus,
	IconTrashOutline18 as TrashIcon,
} from "nucleo-ui-outline-18"
import { useTimezoneSelect } from "react-timezone-select"
import { z } from "zod"

import { Button, IconButton, Input, Select, Tooltip } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import { toast } from "@/components/toast"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { captureError } from "@/lib/capture-error"
import { useSession } from "@/lib/session"
import { dayOptions, timeOptions, timezones } from "@/lib/time"
import { useRole } from "@/lib/useRole"
import { mutateOk, useZero, useZeroWorkspace } from "@/lib/zero"
import { SettingItem } from "../settings-switches/setting-item"
import { SettingsContainer } from "../settings-switches/settings-container"
import { parseResponseTimeHours } from "./parse-response-time-hours"
import {
	hoursForSelection,
	RESPONSE_TIME_CUSTOM,
	RESPONSE_TIME_OPTIONS,
	responseTimeSelection,
} from "./response-time-presets"

const OfficeHoursSchema = z.object({
	days: z.string(),
	startTime: z.string(),
	endTime: z.string(),
})

type OfficeHours = z.infer<typeof OfficeHoursSchema>

const MAX_OFFICE_HOURS = 7

export function OfficeHoursSection() {
	const workspace = useZeroWorkspace()
	const zero = useZero()
	const { role } = useRole()
	const { session } = useSession()
	const ctx = api.useUtils()

	const { options: timezoneOptions } = useTimezoneSelect({
		labelStyle: "original",
		timezones,
	})

	const [officeHours, setOfficeHours] = useState<OfficeHours[]>(
		workspace?.officeHours && Array.isArray(workspace.officeHours)
			? (workspace.officeHours as OfficeHours[])
			: [],
	)

	useEffect(() => {
		if (workspace?.officeHours && Array.isArray(workspace.officeHours)) {
			setOfficeHours(workspace.officeHours as OfficeHours[])
		} else if (!workspace?.officeHours) {
			setOfficeHours([])
		}
	}, [workspace?.officeHours])

	const addOfficeHours = async () => {
		if (officeHours.length >= MAX_OFFICE_HOURS) {
			toast.error({
				title: "Maximum office hours reached",
				description: `You can only add up to ${MAX_OFFICE_HOURS} office hours entries.`,
			})
			return
		}

		const newOfficeHours = [
			...officeHours,
			{ days: "Weekdays", startTime: "9:00 AM", endTime: "5:00 PM" },
		]
		setOfficeHours(newOfficeHours)

		if (session?.workspaceId) {
			const result = await zero.mutate(
				mutators.workspaces.update({
					id: session.workspaceId,
					officeHours: newOfficeHours,
				}),
			).client

			if (result.type === "success") {
				await ctx.workspace.info.invalidate()
				analytics.track("update-office-hours")
				toast.success({ title: "Office hours updated" })
			} else {
				console.error("Error updating office hours:", result.error)
				captureError(result.error, {
					domain: "general",
					action: "update office hours",
					workspaceId: session.workspaceId,
					userId: session.userId,
				})
				toast.error({
					title: "Error",
					description: "Failed to update office hours. Please try again.",
				})
				setOfficeHours(officeHours)
			}
		}
	}

	const updateOfficeHours = async (
		index: number,
		field: keyof OfficeHours,
		value: string,
	) => {
		const updated = [...officeHours]
		updated[index] = { ...updated[index], [field]: value } as OfficeHours
		const previousOfficeHours = [...officeHours]
		setOfficeHours(updated)

		if (session?.workspaceId) {
			const result = await zero.mutate(
				mutators.workspaces.update({
					id: session.workspaceId,
					officeHours: updated,
				}),
			).client

			if (result.type === "success") {
				await ctx.workspace.info.invalidate()
				analytics.track("update-office-hours")
			} else {
				console.error("Error updating office hours:", result.error)
				captureError(result.error, {
					domain: "general",
					action: "update office hours",
					workspaceId: session.workspaceId,
					userId: session.userId,
				})
				toast.error({
					title: "Error",
					description: "Failed to update office hours. Please try again.",
				})
				setOfficeHours(previousOfficeHours)
			}
		}
	}

	const removeOfficeHours = async (index: number) => {
		const updated = officeHours.filter((_, i) => i !== index)
		const previousOfficeHours = [...officeHours]
		setOfficeHours(updated)

		if (session?.workspaceId) {
			const result = await zero.mutate(
				mutators.workspaces.update({
					id: session.workspaceId,
					officeHours: updated,
				}),
			).client

			if (result.type === "success") {
				await ctx.workspace.info.invalidate()
				analytics.track("update-office-hours")
			} else {
				console.error("Error updating office hours:", result.error)
				captureError(result.error, {
					domain: "general",
					action: "update office hours",
					workspaceId: session.workspaceId,
					userId: session.userId,
				})
				toast.error({
					title: "Error",
					description: "Failed to update office hours. Please try again.",
				})
				setOfficeHours(previousOfficeHours)
			}
		}
	}

	const updateMaxResponseTime = async (value: number | null) => {
		if (session) {
			await mutateOk(
				zero.mutate(
					mutators.workspaces.update({
						id: session?.workspaceId,
						maxResponseTimeHours: value,
					}),
				),
			)
			await ctx.workspace.info.invalidate()
			await ctx.user.getMembership.invalidate()
			analytics.track("update-response-time-settings")
		}
	}

	const savedResponseTime = workspace?.maxResponseTimeHours ?? null
	const [customResponseTime, setCustomResponseTime] = useState(false)
	const responseTimeValue = responseTimeSelection(
		savedResponseTime,
		customResponseTime,
	)
	const [responseTimeDraft, setResponseTimeDraft] = useState(
		savedResponseTime?.toString() ?? "",
	)

	useEffect(() => {
		setResponseTimeDraft(savedResponseTime?.toString() ?? "")
	}, [savedResponseTime])

	const selectResponseTime = async (value: string) => {
		if (value === RESPONSE_TIME_CUSTOM) {
			setCustomResponseTime(true)
			return
		}
		setCustomResponseTime(false)
		const hours = hoursForSelection(value)
		if (hours !== savedResponseTime) await updateMaxResponseTime(hours)
	}

	const commitResponseTime = async () => {
		const hours = parseResponseTimeHours(responseTimeDraft)
		if (hours === null) {
			setResponseTimeDraft(savedResponseTime?.toString() ?? "")
			if (savedResponseTime === null) setCustomResponseTime(false)
			return
		}
		setResponseTimeDraft(hours.toString())
		if (hours !== savedResponseTime) await updateMaxResponseTime(hours)
	}

	if (!workspace) return null

	return (
		<>
			<SettingsContainer>
				<SettingItem
					title="Response time"
					description="Set the maximum time you should respond to a message."
					action={
						<div className="flex items-center gap-2">
							{responseTimeValue === RESPONSE_TIME_CUSTOM && (
								<Input
									name="maxResponseTimeHours"
									aria-label="Hours"
									placeholder="24"
									value={responseTimeDraft}
									onChange={(e) => setResponseTimeDraft(e.target.value)}
									onBlur={commitResponseTime}
									onKeyDown={(e) => {
										if (e.key === "Enter") e.currentTarget.blur()
									}}
									type="number"
									min={1}
									readOnly={role === "VIEWER"}
									autoFocus={customResponseTime}
									suffix="hours"
									inputClassName="pr-14"
									className="w-28"
								/>
							)}
							<Select
								name="maxResponseTime"
								showErrorMessage={false}
								options={RESPONSE_TIME_OPTIONS}
								value={responseTimeValue}
								onChange={selectResponseTime}
								disabled={role === "VIEWER"}
								className="w-32"
								selectClassName="dark:bg-gray-700/70"
							/>
						</div>
					}
				/>
			</SettingsContainer>

			{/* Timezone & Office Hours Settings */}
			<div className="mt-4">
				<SettingsContainer>
					<SettingItem
						title="Timezone"
						description="Set your timezone."
						action={
							<Select
								showErrorMessage={false}
								options={timezoneOptions}
								value={workspace?.timezone || "Europe/Berlin"}
								name="timezone"
								className="w-52 overflow-hidden p-0.5 sm:w-80"
								selectClassName="dark:bg-gray-700/70"
								searchable
								searchPlaceholder="Search timezones..."
								onChange={async (value) => {
									if (session) {
										await mutateOk(
											zero.mutate(
												mutators.workspaces.update({
													id: session.workspaceId,
													timezone: value,
												}),
											),
										)
										toast.success({
											title: "Timezone updated",
										})
										analytics.track("update-timezone")
										await ctx.workspace.info.invalidate()
									}
								}}
							/>
						}
					/>
					<SettingItem
						title="Office hours"
						description={`Set your team's availability to reply, based on your workspace's timezone (${(timezones as Record<string, string>)[workspace?.timezone ?? "Europe/Berlin"] ?? "Europe/Berlin"}) - ${officeHours.length}/${MAX_OFFICE_HOURS}`}
						className="flex-col items-start justify-between pt-4"
						action={
							<>
								{officeHours.length > 0 && (
									<div className="space-y-4 pt-4">
										{officeHours.map((hours, index) => (
											<div
												key={index}
												className="group flex flex-col items-start gap-3 sm:flex-row sm:items-center"
											>
												<Select
													name={`officeHours.${index}.days`}
													value={hours.days}
													onChange={async (value: string) => {
														await updateOfficeHours(index, "days", value)
													}}
													options={dayOptions}
													className="w-32"
													selectClassName="dark:bg-gray-700/70"
												/>

												<div className="flex items-center gap-2 sm:gap-3">
													<Select
														name={`officeHours.${index}.startTime`}
														value={hours.startTime}
														onChange={async (value: string) => {
															await updateOfficeHours(index, "startTime", value)
														}}
														options={timeOptions}
														className="w-24 sm:w-32"
														selectClassName="dark:bg-gray-700/70"
													/>

													<span className="hidden text-secondary md:block">
														to
													</span>

													<Select
														name={`officeHours.${index}.endTime`}
														value={hours.endTime}
														onChange={async (value: string) => {
															await updateOfficeHours(index, "endTime", value)
														}}
														options={timeOptions}
														className="w-24 sm:w-32"
														selectClassName="dark:bg-gray-700/70"
													/>

													<Tooltip label="Delete time slot">
														<IconButton
															variant="outline"
															className="opacity-0 group-hover:opacity-100"
															onClick={() => void removeOfficeHours(index)}
															Icon={TrashIcon}
														/>
													</Tooltip>
												</div>
											</div>
										))}
									</div>
								)}
								<Tooltip label="Add new time slot">
									<Button
										variant="outline"
										onClick={() => void addOfficeHours()}
										title={
											officeHours.length >= MAX_OFFICE_HOURS
												? `Maximum ${MAX_OFFICE_HOURS} office hours reached`
												: "Add office hours"
										}
										Icon={Plus}
									>
										Add hours
									</Button>
								</Tooltip>
							</>
						}
					/>
				</SettingsContainer>
			</div>
		</>
	)
}
